export const STORY_KEYS = ["my_vibe", "my_faves", "more_about_me"] as const;

export type StoryKey = (typeof STORY_KEYS)[number];

export const STORY_COLORS = [
  { id: "lime", label: "Lime", swatch: "#c2df4f", from: "#f0f3c8", to: "#c2df4f" },
  { id: "yellow", label: "Yellow", swatch: "#ffe81f", from: "#fff6b0", to: "#ffe81f" },
  { id: "pink", label: "Pink", swatch: "#fe8391", from: "#ffd0d6", to: "#fe8391" },
  { id: "indigo", label: "Indigo", swatch: "#7178ff", from: "#e8e8ff", to: "#7178ff" },
  { id: "blue", label: "Blue", swatch: "#2ed2ff", from: "#c8f3ff", to: "#2ed2ff" },
  { id: "purple", label: "Purple", swatch: "#9e40fd", from: "#e4ccff", to: "#9e40fd" },
  { id: "orange", label: "Orange", swatch: "#ffac40", from: "#ffe4c2", to: "#ffac40" },
] as const;

export type StoryColorId = (typeof STORY_COLORS)[number]["id"];

export const EMPTY_STORY_GRADIENT = { from: "#f8f7f5", to: "#dfddd8" };

interface StorySlot {
  slot: string;
  label: string;
}

interface StoryMeta {
  index: string;
  number: number;
  title: string;
  blurb: string;
  eyebrow: string;
  captions: readonly string[];
  slots: readonly StorySlot[];
}

export const STORY_META: Record<StoryKey, StoryMeta> = {
  my_vibe: {
    index: "01",
    number: 1,
    title: "My vibe",
    blurb: "This page helps teens get to know you and your style.",
    eyebrow: "my vibe",
    captions: [
      "Suuupa Goofy",
      "Big Time Chill",
      "Happy Camper",
      "Low-Key Weird",
    ],
    slots: [
      { slot: "fav_meme", label: "my fave meme" },
      { slot: "me_in_the_wild", label: "me in the wild" },
      { slot: "dream_vacation", label: "my dream vacation location" },
      { slot: "movie_tv_character", label: "my movie/tv character vibe" },
    ],
  },
  my_faves: {
    index: "02",
    number: 2,
    title: "My faves",
    blurb: "This page helps teens learn about your favorite things!",
    eyebrow: "my faaavs",
    captions: [
      "What I'm Loving RN",
      "The Good Stuff",
      "Latest Obsessions",
      "On Repeat Lately",
    ],
    slots: [
      { slot: "fave_snack", label: "My fave snack" },
      { slot: "fave_album", label: "My fave album" },
      { slot: "movie_tv_1", label: "Movie / TV show" },
      { slot: "movie_tv_2", label: "Movie / TV show" },
      { slot: "go_to_beverage", label: "My go-to beverage" },
    ],
  },
  more_about_me: {
    index: "03",
    number: 3,
    title: "More about me",
    blurb: "One photo that matches the caption you pick.",
    eyebrow: "more about meee",
    captions: [
      "Me When I Was Little",
      "A Proud Moment",
      "Something I've Made",
      "Me and My Bestie",
    ],
    slots: [{ slot: "photo", label: "photo" }],
  },
};

export interface StoryPhoto {
  slot: string;
  key: string;
  url: string;
}

export interface StorySection {
  color: StoryColorId | null;
  caption: string | null;
  photos: StoryPhoto[];
}

export interface StoryFrame {
  slot: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotate?: number;
  radius?: number;
  border?: number;
  hole?: number;
}

export interface StoryDecal {
  src: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export const STORY_DESIGN = { height: 480, width: 270 };
// Story player uses cover on the full screen. 324×706 is iPhone 17 (470×1024).
export const STORY_STAGE = { height: 706, width: 324 };

export const STORY_LAYOUT: Record<
  StoryKey,
  {
    align: "start" | "center";
    behind: StoryDecal[];
    captionSize: number;
    frames: StoryFrame[];
    front: StoryDecal[];
    offsetY: number;
  }
> = {
  my_vibe: {
    align: "start",
    captionSize: 27,
    offsetY: 0,
    behind: [
      { h: 122.9, src: "/mentor-stories/scribble-pink.png", w: 140, x: 13.99, y: 103.99 },
      { h: 40, src: "/mentor-stories/sticker-kindness.png", w: 40, x: 212.01, y: 60 },
    ],
    frames: [
      { h: 134.39, radius: 14, rotate: -3, slot: "fav_meme", w: 129.59, x: 10.81, y: 187.19 },
      { h: 143.99, radius: 14, rotate: 4, slot: "me_in_the_wild", w: 124.19, x: 137.7, y: 139.19 },
      { h: 153.59, radius: 14, rotate: -2, slot: "movie_tv_character", w: 126.89, x: 135, y: 287.99 },
      { h: 124.79, radius: 14, rotate: 3, slot: "dream_vacation", w: 94.5, x: 35.09, y: 321.6 },
    ],
    front: [
      { h: 37.42, src: "/mentor-stories/sticker-happy.png", w: 43.98, x: 3.99, y: 196 },
      { h: 34.53, src: "/mentor-stories/sticker-smile.png", w: 33.99, x: 232.02, y: 246 },
      { h: 45.99, src: "/mentor-stories/sticker-hello.png", w: 46, x: 3.99, y: 416.01 },
    ],
  },
  my_faves: {
    align: "start",
    captionSize: 27,
    offsetY: 0,
    behind: [],
    frames: [
      { h: 124, hole: 28, radius: 62, slot: "fave_album", w: 124, x: 164.7, y: 176 },
      { h: 120.45, radius: 14, rotate: -2, slot: "fave_snack", w: 89.54, x: 18.68, y: 180.6 },
      { border: 1, h: 110.4, radius: 8, rotate: -8, slot: "go_to_beverage", w: 78.3, x: 24.3, y: 334.4 },
      { h: 120.44, radius: 14, rotate: 5, slot: "movie_tv_1", w: 86.84, x: 177.95, y: 319.8 },
      { border: 1, h: 124.79, radius: 8, rotate: 5, slot: "movie_tv_2", w: 81, x: 72.9, y: 363.2 },
    ],
    front: [],
  },
  more_about_me: {
    align: "start",
    captionSize: 22,
    offsetY: 0,
    behind: [
      { h: 90.25, src: "/mentor-stories/spark-left.png", w: 70.2, x: 17.12, y: 121 },
      { h: 84.67, src: "/mentor-stories/spark-right.png", w: 62.09, x: 188.79, y: 366 },
    ],
    frames: [
      { border: 5, h: 225.6, radius: 12, slot: "photo", w: 167.39, x: 52.13, y: 176.09 },
    ],
    front: [],
  },
};

export interface StorySlide {
  key: string;
  url: string;
  order: number;
  header: string | null;
  eyebrow: string | null;
}

export interface StoryDraft {
  my_vibe: StorySection;
  my_faves: StorySection;
  more_about_me: StorySection;
  slides: StorySlide[];
}

export interface StoryComment {
  section: StoryKey;
  note: string;
  status: "open" | "resolved";
  photo_keys?: string[];
}

export const emptyDraft = (): StoryDraft => ({
  more_about_me: { caption: null, color: null, photos: [] },
  my_faves: { caption: null, color: null, photos: [] },
  my_vibe: { caption: null, color: null, photos: [] },
  slides: [],
});

const sectionSignature = (section: StorySection) =>
  [
    section.color ?? "",
    section.caption ?? "",
    section.photos
      .map((photo) => `${photo.slot}:${photo.key}`)
      .sort()
      .join(","),
  ].join("|");

export const draftEdited = (current: StoryDraft, baseline: StoryDraft) =>
  STORY_KEYS.some(
    (key) => sectionSignature(current[key]) !== sectionSignature(baseline[key]),
  );

export const setStoryPhoto = (
  draft: StoryDraft,
  key: StoryKey,
  slot: string,
  photo?: StoryPhoto,
): StoryDraft => ({
  ...draft,
  [key]: {
    ...draft[key],
    photos: [
      ...draft[key].photos.filter((item) => item.slot !== slot),
      ...(photo ? [photo] : []),
    ],
  },
});

export const wrapCaption = (
  text: string,
  maxWidth: number,
  measure: (value: string) => number,
): string[] => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (line && measure(next) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
};

export const isStoryKey = (value: string): value is StoryKey =>
  (STORY_KEYS as readonly string[]).includes(value);

export const storyColor = (id: string | null | undefined) =>
  STORY_COLORS.find((color) => color.id === id);

export const photoKeys = (section: StorySection, key: StoryKey): string[] =>
  STORY_META[key].slots.map(
    (slot) => section.photos.find((photo) => photo.slot === slot.slot)?.key.trim() ?? "",
  );

export const sectionComplete = (draft: StoryDraft, key: StoryKey): boolean => {
  const section = draft[key];
  if (!section.color || !section.caption?.trim()) return false;
  return photoKeys(section, key).every((photoKey) => photoKey.length > 0);
};

export const storyLocked = (
  draft: StoryDraft,
  key: StoryKey,
  navUnlocked: boolean,
) => {
  if (navUnlocked) return false;
  const previous = STORY_KEYS[STORY_KEYS.indexOf(key) - 1];
  return previous ? !sectionComplete(draft, previous) : false;
};

export const storyReady = (draft: StoryDraft): boolean =>
  STORY_KEYS.every((key) => sectionComplete(draft, key));

export const filledPhotoCount = (section: StorySection, key: StoryKey): number =>
  photoKeys(section, key).filter((photoKey) => photoKey.length > 0).length;

const sameKeys = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

export const commentStillBlocks = (
  draft: StoryDraft,
  comment: StoryComment,
): boolean => {
  if (comment.status !== "open") return false;
  if (!comment.photo_keys) return true;
  return sameKeys(
    photoKeys(draft[comment.section], comment.section),
    comment.photo_keys,
  );
};

export const blockedStoryKeys = (
  draft: StoryDraft,
  comments: readonly StoryComment[],
): StoryKey[] =>
  STORY_KEYS.filter((key) =>
    comments.some(
      (comment) => comment.section === key && commentStillBlocks(draft, comment),
    ),
  );

export const openNote = (
  comments: readonly StoryComment[],
  key: StoryKey,
): StoryComment | undefined =>
  comments.find(
    (comment) =>
      comment.section === key && comment.status === "open" && comment.note.trim(),
  );
